import type {ToolCallMessagePartStatus} from '@assistant-ui/react';
import {type IconName} from '@wandb/components';
import React from 'react';

import {WBAgentIcon, type WBAgentIconColor} from '../WBAgentIcons';
import {WBAgentStepDisclosure} from '../WBAgentStepDisclosure';
import type {MemoryEntry} from './toolCall';

/** MemoryNoticeContent is everything a notice shows for one tool call. */
export type MemoryNoticeContent = {
  icon: IconName;
  color: WBAgentIconColor;
  label: string;
  refusal?: string;
  memories?: MemoryEntry[];
  missing?: string[];
};

/**
 * MemoryNotice shows what a memory call did, so the user can see what the agent
 * recorded about their project.
 */
export const MemoryNotice: React.FC<{
  content: MemoryNoticeContent;
  status: ToolCallMessagePartStatus;
  toolName: string;
}> = ({content, status, toolName}) => {
  // NOTE: A cancelled write may have already applied, so keep the label and
  // only dim it.
  const isCancelled =
    status.type === 'incomplete' && status.reason === 'cancelled';

  return (
    <div className={isCancelled ? 'opacity-60' : undefined}>
      {noticeHasDetail(content) ? (
        <WBAgentStepDisclosure
          icon={content.icon}
          iconColor={content.color}
          label={content.label}
          stepKind="tool_call"
          toolName={toolName}
          panelClassName="flex flex-col gap-8 border-l-2 border-wb-divider pl-12">
          <NoticeDetail content={content} />
        </WBAgentStepDisclosure>
      ) : (
        <NoticeLine content={content} />
      )}
    </div>
  );
};

/**
 * NoticeLine is a plain row for a call that has no detail to expand.
 *
 * NOTE: Padding matches `WBAgentStepDisclosure` so the icon lines up with the
 * rows above and below it.
 */
const NoticeLine: React.FC<{content: MemoryNoticeContent}> = ({content}) => (
  <div className="flex w-full items-center gap-8 px-6 py-4 text-left text-wb-text-primary">
    <span className="inline-flex shrink-0">
      <WBAgentIcon icon={content.icon} size="sm" color={content.color} />
    </span>
    <span className="text-left leading-[21px] font-semibold text-sm">
      {content.label}
    </span>
  </div>
);

const NoticeDetail: React.FC<{content: MemoryNoticeContent}> = ({content}) => {
  const {refusal, memories = [], missing = []} = content;
  if (refusal) {
    return <span className="text-sm text-wb-text-secondary">{refusal}</span>;
  }

  return (
    <>
      {memories.map(memory => (
        <div key={memory.name} className="flex flex-col gap-4">
          <span className="text-sm font-[Inconsolata] text-wb-text-secondary">
            {memory.name}
          </span>
          {memory.description && (
            <span className="text-sm text-wb-text-secondary">
              {memory.description}
            </span>
          )}
        </div>
      ))}
      {missing.length > 0 && (
        <span className="text-sm text-wb-text-tertiary">
          Not found: {missing.join(', ')}
        </span>
      )}
    </>
  );
};

function noticeHasDetail(content: MemoryNoticeContent): boolean {
  return Boolean(
    content.refusal || content.memories?.length || content.missing?.length
  );
}
